/**
 * Deleting the stored ads token — the one destructive action on this tab.
 *
 * Two presses, inline, the same shape `WhatsAppDisconnect` uses. What a
 * confirmation has to say is not "are you sure" but **what is lost**, and here
 * that is the sealed token: Wend keeps no copy, and Meta will not show a system
 * user token twice either, so reconnecting almost always means minting a new
 * one. The confirming button's own label says that rather than saying
 * "Confirm".
 *
 * ## IT IS A BUTTON HERE AND A NESTED CARD ON THE WHATSAPP TAB
 *
 * Not an inconsistency. That card sits inside an Advanced disclosure with
 * several other things and needs its own edges to be separable from them; this
 * tab has three buttons in one row and a bordered card among them would be the
 * heaviest object on a healthy connection.
 *
 * **Nothing about the two presses or the wording is softened to suit the
 * smaller space.** A confirmation trimmed to fit is a confirmation that has
 * stopped doing its job.
 *
 * It is its own file because it holds a request: importing the API client
 * reaches `lib/config.ts` and `import.meta.env`, which evaluates only inside
 * Vite, and a component that imported this could not be rendered by a verify
 * script at all.
 */
import { Button, useToast } from '@wend/ui';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import { disconnectMetaAds, type MetaAdsIntegration } from '../../lib/api/meta-ads';
import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';

export function MetaAdsDisconnect({
  integration,
  onDisconnected,
}: {
  integration: MetaAdsIntegration;
  onDisconnected: () => void;
}) {
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);

  async function onDisconnect(): Promise<void> {
    if (pending) return;
    setFailure(null);
    setPending(true);
    try {
      await disconnectMetaAds();
      toast({
        tone: 'success',
        title: 'Meta ads disconnected',
        description: 'The stored token was deleted.',
      });
      onDisconnected();
    } catch (error) {
      setFailure(requestFailure(error));
      setPending(false);
    }
  }

  if (!confirming) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          setConfirming(true);
        }}
      >
        <Trash2 aria-hidden="true" className="size-4" />
        Disconnect
      </Button>
    );
  }

  return (
    <div data-role="meta-ads-disconnect-confirm" className="flex w-full flex-col gap-3">
      {failure !== null && <RequestFailureAlert failure={failure} />}

      <p className="max-w-prose text-[0.92rem] leading-relaxed text-text">
        Disconnect ad account{' '}
        <span className="font-mono text-[0.88rem]">
          {integration.accountName ?? integration.adAccountId}
        </span>
        ? The sealed token is deleted and Wend cannot recover it. Meta will not show that token
        again either, so reconnecting most likely means creating a new one. Nothing running in Ads
        Manager is stopped or changed.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={pending}
          className="border-bad/45 text-bad hover:border-bad/70 hover:bg-bad/10"
          onClick={() => {
            void onDisconnect();
          }}
        >
          <Trash2 aria-hidden="true" className="size-4" />
          {pending ? 'Disconnecting' : 'Yes, disconnect and delete the token'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={pending}
          onClick={() => {
            setConfirming(false);
          }}
        >
          Keep it connected
        </Button>
      </div>
    </div>
  );
}
